/* Anomaly findings: what the statistics decided, and — separately, and only when a model was asked —
 * what it said about a decision already made.
 *
 * **Four verdicts, four appearances.** `insufficient_history` is a cold start and `undetermined` is a
 * series the maths did not apply to. Rendering either as "normal" would report an unmeasured table as
 * healthy; rendering `undetermined` as "anomalous" would page somebody about a table loading exactly
 * 1,000 rows a day. Both are the platform lying about a customer's data.
 *
 * **The verdict filter narrows the table; it never hides a whole verdict from the KPI row.** The four
 * cards always show every count including the zeros — "0 anomalous, 3 insufficient history" is the
 * cold-start answer, and omitting empty buckets makes it read like "3 normal". "Hide muted series" is
 * on by default: a muted series is one somebody already decided not to be paged about.
 *
 * **The page is bounded.** `GET /findings` returns the 50 most recent and has no cursor, so on a
 * large estate there are older findings this screen cannot reach — the hint says "the 50 most recent"
 * rather than "50 findings" when the page is full.
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useDatasetNames } from '@/features/catalog-browser';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { CheckboxField } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import type { SegmentOption } from '@/shared/ui/SegmentedFilter';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { FINDINGS_PAGE_SIZE, useAnomalyCapabilities, useFindings } from '../api/anomaly';
import type { Explanation, StoredFinding, Verdict } from '../api/anomaly';
import {
  VERDICT_FILTERS,
  deviationCell,
  explanationKey,
  isModelAuthored,
  kpiTone,
  matchesVerdict,
  nameGapKey,
  signalKey,
  sortedByPriority,
  summaryCounts,
  summaryKey,
  verdictKey,
  verdictTone,
} from '../model/presentation';
import type { VerdictFilter } from '../model/presentation';

function HowItDecides(): ReactElement | null {
  const { t } = useTranslation();
  const capabilities = useAnomalyCapabilities();
  if (!capabilities.isSuccess) return null;
  const { rung, source_access, detection_method, model_role, signals } =
    capabilities.data;

  return (
    <details>
      <summary className="lq-muted" style={{ cursor: 'pointer' }}>
        {t('anomaly.method.title')}
      </summary>
      <div className="lq-stack" style={{ marginTop: 'var(--space-3)' }}>
        <p className="lq-muted">
          {t('anomaly.method.rung', { rung, access: source_access })}
        </p>
        <p>
          <span className="lq-muted">{t('anomaly.method.detection')}</span>{' '}
          {detection_method}
        </p>
        <p>
          <span className="lq-muted">{t('anomaly.method.model')}</span> {model_role}
        </p>
        <DataTable
          caption={t('anomaly.method.tableCaption')}
          columns={[
            { key: 'signal', label: t('anomaly.method.column.signal') },
            { key: 'watches', label: t('anomaly.method.column.watches') },
            {
              key: 'threshold',
              label: t('anomaly.method.column.threshold'),
              numeric: true,
            },
            { key: 'minimum', label: t('anomaly.method.column.minimum'), numeric: true },
          ]}
        >
          {signals.map((signal) => (
            <tr key={signal.code}>
              <td>{t(signalKey(signal.code))}</td>
              <td>
                {signal.watches
                  .map((direction) => t(`anomaly.watches.${direction}`))
                  .join(', ')}
              </td>
              <td className="lq-table__num lumiq-numeric">{signal.threshold}</td>
              <td className="lq-table__num lumiq-numeric">
                {signal.minimum_observations}
              </td>
            </tr>
          ))}
        </DataTable>
      </div>
    </details>
  );
}

function VerdictKpi({
  verdict,
  count,
}: {
  readonly verdict: Verdict;
  readonly count: number;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={`lq-vkpi lq-vkpi--${kpiTone(verdict)}`}>
      <span className="lq-vkpi__label">
        <span className="lq-vkpi__dot" />
        {t(verdictKey(verdict))}
      </span>
      <span className="lq-vkpi__value lumiq-numeric">{count}</span>
      <span className="lq-vkpi__sub">{t(summaryKey(verdict))}</span>
    </div>
  );
}

/** The explanation popup, opened from the "View" button in the table's Explanation column.
 *
 * Shows the full text plus its status (`model_accepted` / `rejected` / `template_only` /
 * `unavailable`) and who authored it — the same facts the row used to carry inline, now behind a
 * click so the table stays scannable. */
function ExplanationDialog({
  finding,
  seriesLabel,
  onClose,
}: {
  readonly finding: StoredFinding;
  /** "{dataset name} · {signal}" — the dataset resolved to its name, as in the table's first
   * column, so the dialog never shows a bare UUID. */
  readonly seriesLabel: string;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { explanation } = finding;
  const author = isModelAuthored(explanation)
    ? t('anomaly.explanation.byModel', { validator: explanation.validator ?? '' })
    : t('anomaly.explanation.byPlatform');
  return (
    <Modal
      open
      title={t('anomaly.explanation.dialogTitle')}
      hint={seriesLabel}
      onClose={onClose}
    >
      <p style={{ whiteSpace: 'pre-wrap', marginTop: 0 }}>{explanation.text}</p>
      <p className="lq-dim">
        {author} — {t(explanationKey(explanation))}
        {explanation.rejection_reason != null && (
          <>
            {' ('}
            <span className="lq-mono">{explanation.rejection_reason}</span>
            {')'}
          </>
        )}
      </p>
    </Modal>
  );
}

function Authorship({
  explanation,
}: {
  readonly explanation: Explanation;
}): ReactElement {
  const { t } = useTranslation();
  const authored = isModelAuthored(explanation);
  return (
    <span
      className={authored ? 'lq-authorship lq-authorship--model' : 'lq-authorship'}
      title={
        authored
          ? t('anomaly.explanation.byModel', { validator: explanation.validator ?? '' })
          : t('anomaly.explanation.byPlatform')
      }
    >
      {authored ? t('anomaly.authored.model') : t('anomaly.authored.host')}
    </span>
  );
}

/** How long ago, in words, from an ISO instant — coarse on purpose (a findings table is scanned,
 * not read to the minute). */
function ago(iso: string, t: ReturnType<typeof useTranslation>['t']): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return t('anomaly.detected.now');
  if (minutes < 60) return t('anomaly.detected.minutes', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('anomaly.detected.hours', { count: hours });
  return t('anomaly.detected.days', { count: Math.round(hours / 24) });
}

function FindingsTable({
  findings,
  names,
  namesReadable,
  namesTruncated,
}: {
  readonly findings: readonly StoredFinding[];
  readonly names: ReadonlyMap<string, string>;
  readonly namesReadable: boolean;
  readonly namesTruncated: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const ordered = sortedByPriority(findings);
  const [viewing, setViewing] = useState<StoredFinding | null>(null);

  const seriesLabel = (finding: StoredFinding): string => {
    const name =
      names.get(finding.dataset_id) ?? t(nameGapKey(namesReadable, namesTruncated));
    return `${name} · ${t(signalKey(finding.signal))}`;
  };

  return (
    <>
      <DataTable
        caption={t('anomaly.tableCaption')}
        columns={[
          { key: 'series', label: t('anomaly.column.series') },
          { key: 'verdict', label: t('anomaly.column.verdict') },
          { key: 'deviation', label: t('anomaly.column.deviation'), numeric: true },
          { key: 'explanation', label: t('anomaly.column.explanation') },
          { key: 'detected', label: t('anomaly.column.detected') },
          { key: 'authored', label: t('anomaly.column.authored') },
          { key: 'open', label: t('anomaly.column.open'), actions: true },
        ]}
      >
        {ordered.map((finding) => (
          <tr
            key={finding.id}
            className={
              finding.verdict === 'anomalous' ? 'lq-table__row--flag' : undefined
            }
          >
            <td title={finding.dataset_id}>
              {names.has(finding.dataset_id) ? (
                <span className="lq-mono">
                  {names.get(finding.dataset_id)} · {t(signalKey(finding.signal))}
                </span>
              ) : (
                <span className="lq-dim">
                  {t(nameGapKey(namesReadable, namesTruncated))}
                </span>
              )}
              {finding.muted && (
                <span className="lq-dim"> · {t('anomaly.mutedTag')}</span>
              )}
            </td>
            <td>
              <StatusBadge
                tone={verdictTone(finding.verdict)}
                label={t(verdictKey(finding.verdict))}
                title={t(`${verdictKey(finding.verdict)}.meaning`)}
              />
            </td>
            <td className="lq-table__num lumiq-numeric">{deviationCell(finding)}</td>
            <td>
              <Button
                size="compact"
                onClick={() => {
                  setViewing(finding);
                }}
              >
                {t('anomaly.explanation.view')}
              </Button>
            </td>
            <td className="lq-dim">{ago(finding.evaluated_at, t)}</td>
            <td>
              <Authorship explanation={finding.explanation} />
            </td>
            <td className="lq-table__actions">
              <Link to={`/anomalies/${encodeURIComponent(finding.id)}`}>
                {t('anomaly.open')}
              </Link>
            </td>
          </tr>
        ))}
      </DataTable>
      {viewing !== null && (
        <ExplanationDialog
          finding={viewing}
          seriesLabel={seriesLabel(viewing)}
          onClose={() => {
            setViewing(null);
          }}
        />
      )}
    </>
  );
}

export function AnomaliesScreen(): ReactElement {
  const { t } = useTranslation();
  const names = useDatasetNames();
  const findings = useFindings();
  const [filter, setFilter] = useState<VerdictFilter>('all');
  const [hideMuted, setHideMuted] = useState(true);

  const all = useMemo<readonly StoredFinding[]>(
    () => findings.data ?? [],
    [findings.data],
  );
  const counts = useMemo(() => summaryCounts(all), [all]);
  const shown = useMemo(
    () =>
      all.filter(
        (finding) => matchesVerdict(finding, filter) && (!hideMuted || !finding.muted),
      ),
    [all, filter, hideMuted],
  );

  const lastRan = all.reduce<string | null>(
    (latest, finding) =>
      latest === null || finding.evaluated_at > latest ? finding.evaluated_at : latest,
    null,
  );
  const seriesUnderWatch = new Set(
    all.map((finding) => `${finding.dataset_id}:${finding.signal}`),
  ).size;

  const filterOptions: readonly SegmentOption<VerdictFilter>[] = VERDICT_FILTERS.map(
    (value) => ({
      id: value,
      label: value === 'all' ? t('anomaly.filter.all') : t(verdictKey(value)),
    }),
  );

  return (
    <>
      <PageHeader
        title={t('anomaly.title')}
        lede={t('anomaly.lede')}
        meta={
          findings.isSuccess ? (
            <span>
              {lastRan === null
                ? t('anomaly.meta.neverRun')
                : t('anomaly.meta.lastRan', { ago: ago(lastRan, t) })}
              {' · '}
              {t('anomaly.meta.underWatch', { count: seriesUnderWatch })}
            </span>
          ) : undefined
        }
      />

      <div className="lq-stack">
        {findings.isError && (
          <Alert tone="error" title={t('anomaly.unreadable')}>
            {findings.error.message}
          </Alert>
        )}

        {findings.isPending && <SkeletonRows rows={5} columns={6} />}

        {findings.isSuccess && (
          <>
            <div className="lq-vkpi-row">
              {counts.map(({ verdict, count }) => (
                <VerdictKpi key={verdict} verdict={verdict} count={count} />
              ))}
            </div>

            {all.length === 0 ? (
              <EmptyState
                title={t('anomaly.emptyTitle')}
                guidance={t('anomaly.emptyGuidance')}
              />
            ) : (
              <Card
                title={t('anomaly.recent')}
                hint={t(
                  all.length >= FINDINGS_PAGE_SIZE
                    ? 'anomaly.recentHintCapped'
                    : 'anomaly.recentHint',
                  { total: all.length, shown: shown.length },
                )}
                flush
              >
                <div className="lq-anomaly-filterbar">
                  <span className="lq-anomaly-filterbar__label">
                    {t('anomaly.filter.verdict')}
                  </span>
                  <SegmentedFilter
                    label={t('anomaly.filter.label')}
                    options={filterOptions}
                    value={filter}
                    onChange={setFilter}
                  />
                  <span className="lq-anomaly-filterbar__spacer" />
                  <CheckboxField
                    label={t('anomaly.filter.hideMuted')}
                    checked={hideMuted}
                    onChange={setHideMuted}
                  />
                </div>
                {shown.length === 0 ? (
                  <div className="lq-card__body">
                    <EmptyState
                      title={t('anomaly.filteredEmptyTitle')}
                      guidance={t('anomaly.filteredEmptyGuidance')}
                    />
                  </div>
                ) : (
                  <FindingsTable
                    findings={shown}
                    names={names.data?.byId ?? new Map<string, string>()}
                    namesReadable={names.isSuccess}
                    namesTruncated={names.data?.truncated ?? false}
                  />
                )}
              </Card>
            )}

            <HowItDecides />
          </>
        )}
      </div>
    </>
  );
}
